import { api } from '../../../lib/api.js';

/**
 * POST {VITE_API_URL}/v1/login. Legacy body shape is { login_id, pin, keepalive }
 * (see apps/legacy/src/redux/userAuthentication/userAuth.sagas.js:391-397) — we
 * translate from the form's semantic field names here.
 *
 * Success: { status: true, code: 200, data: { user_id, user_orgid, onboarding?, ... } }
 * Failure: { status: false, code: !==200, message: string }
 *
 * @param {{ user_name: string, password: string }} payload
 */
export async function login(payload) {
  const res = await api.request({
    url: 'login',
    method: 'POST',
    data: {
      login_id: payload.user_name,
      pin: payload.password,
      keepalive: true,
    },
  });
  return res.data;
}

/**
 * Social login uses the same legacy /login endpoint as the old React app.
 * Old payload shape:
 * { login_id, user_token, user_name, photo_url, social_domain }.
 *
 * @param {{
 *   loginId: string,
 *   userToken: string,
 *   userName?: string,
 *   photoUrl?: string,
 *   socialDomain: 'google' | 'fb' | string,
 * }} payload
 */
export async function socialLogin(payload) {
  const res = await api.request({
    url: 'login',
    method: 'POST',
    data: {
      login_id: payload.loginId,
      user_token: payload.userToken,
      user_name: payload.userName ?? payload.loginId,
      photo_url: payload.photoUrl ?? '',
      social_domain: payload.socialDomain,
      keepalive: true,
    },
  });
  return res.data;
}

/**
 * OTP-based login (phone or email).
 * @param {string} loginId
 */
export async function requestOtp(loginId) {
  const res = await api.request({
    url: 'otplogin',
    method: 'POST',
    data: { login_id: loginId },
  });
  return res.data;
}

/**
 * Verifies an OTP code. Legacy production verifies with { user_id, otp } after
 * /otplogin returns the user id; login_id is only a defensive fallback.
 * @param {{ user_id?: string|number, login_id?: string, otp: string }} payload
 */
export async function verifyOtp(payload) {
  const res = await api.request({
    url: 'verifyloginotp',
    method: 'POST',
    data: payload,
  });
  return res.data;
}

/**
 * Legacy logout is a best-effort GET. Client storage is still cleared by the
 * sign-out saga even if the backend endpoint is unavailable.
 */
export async function logout() {
  const res = await api.request({ url: 'logout', method: 'GET' });
  return res.data;
}

/**
 * SSO callback validation. Legacy /sso receives a temporary token in the URL,
 * then validates it with GET /v1/validateuser using the custom access-token
 * header. The response data becomes the normal STEP session.
 *
 * @param {string} token
 */
export async function validateSsoToken(token) {
  const res = await api.request({
    url: 'validateuser',
    method: 'GET',
    headers: { 'access-token': token },
  });
  return res.data;
}

/**
 * Legacy "forgot password" — sends an OTP to the registered email or mobile.
 * Endpoint: POST /v1/forgotpassword. Body shape per
 * apps/legacy/src/redux/userAuthentication/userAuth.sagas.js:31-44.
 * No access-token required (user is unauthenticated).
 *
 * @param {string} loginId — the email or phone the account is registered under.
 */
export async function requestPasswordReset(loginId) {
  const res = await api.request({
    url: 'forgotpassword',
    method: 'POST',
    data: { user_name: loginId, old_user_name: null },
  });
  return res.data;
}

/**
 * Apply a license key to the currently-authenticated user. Endpoint:
 * POST /v1/licenses with body { license_key } and access-token header.
 * Per apps/legacy/src/redux/settings/settings.sagas.js:145-167.
 *
 * NOTE: a plain password-login user does not receive a token (only
 * SSO/OTP/social returns data.token). Callers should only invoke this
 * after a successful OTP verify, which does return a token. If
 * localStorage 'token' is missing, this will still send the request but
 * the backend will reject with a 401 — surface that to the user.
 *
 * @param {string} licenseKey
 */
export async function applyLicenseKey(licenseKey) {
  const res = await api.request({
    url: 'licenses',
    method: 'POST',
    data: { license_key: licenseKey },
    headers: { 'access-token': localStorage.getItem('token') ?? '' },
  });
  return res.data;
}
